// Put-Away sub-view for Shelf Drop / Forecast Drop.
//
// Lists totes that have been picked within this module and are
// awaiting put-away to a low location. The user scans a tote, then
// EITHER scans a target low location and confirms the full-tote move,
// which closes the claims via /drop/put-away/complete, or (when the
// warehouse's item-level put-away setting is on, the same
// allow_item_level_put_away flag the main Put Away module uses) scans
// one of the tote's items followed by its location, moving just that
// SKU via /drop/put-away/item-complete. Repeats until the tote is
// empty. Per-warehouse, per-module visibility — any picker in the
// warehouse can finish any tote from their module.
//
// Rescanning the active tote exits back to the tote list (local UI
// close only — the tote keeps its picked stock and stays in the
// queue), mirroring the main Put Away page's tote-rescan close.
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, MapPin, PackageOpen, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import "../../styles/scansheet.css";
import HiddenScanInput from "../HiddenScanInput";
import FitWord from "../FitWord";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import ScannedItemCard from "../put-away-shared/ScannedItemCard";
import LastPutAwayBanner, { type LastPutAwayItem } from "../put-away-shared/LastPutAwayBanner";
import { CONFIRM_ARM_MS } from "../put-away-shared/confirmArming";
import WrongQueueModal from "../put-away-shared/WrongQueueModal";
import { lookupWrongQueue, type WrongQueueHome } from "../put-away-shared/toteWhereabouts";
import { playSuccess, playError, playBoxAdd, playToteFinalised } from "../helpers/audioPlayer";
import { useDemoVisible } from "../helpers/demoMode";
import DropViewToggle from "./DropViewToggle";

// Demo target locations — the low pick-faces the picker drops totes
// into. Mirrors the canonical seed in maintenance.controller.js.
const DEMO_LOW_LOCATIONS = ["NP-A-1-1-1", "NP-A-1-1-2", "NP-A-1-2-1", "NP-A-1-2-2", "NP-A-2-1-1"];
const DEMO_HIGH_LOCATIONS = ["NP-H-A-1-1-1", "NP-H-A-1-1-2", "NP-H-A-1-2-1", "NP-H-A-1-2-2", "NP-H-A-2-1-1"];

type DropModule = "shelf" | "forecast";
type Phase = "awaiting-tote" | "awaiting-location" | "awaiting-item-location";
type ScanStatus = "idle" | "success" | "error" | "complete";

interface QueueItem {
    sku: string;
    qty: number;
    fromLocation: string;
    // Enrichment from the BE queue payload (drop-claims
    // listAwaitingPutAway): product title + the full scannable-barcode
    // set (products.barcode + sku_barcodes alternates) so item scans
    // resolve locally without a per-scan round-trip.
    title?: string | null;
    thumbnailUrl?: string | null;
    imageUrl?: string | null;
    barcodes?: string[];
}
interface QueueTote {
    tote_id: string;
    picked_by: string | null;
    first_picked_at: string | null;
    last_picked_at: string | null;
    items: QueueItem[];
}

// Per-SKU rollup of a tote's contents. A tote can hold multiple claim
// rows for the same SKU (picked from different source locations) —
// operators see and scan the SKU once, with the summed quantity.
interface ToteSkuLine {
    sku: string;
    qty: number;
    title: string | null;
    thumbnailUrl: string | null;
    imageUrl: string | null;
    barcodes: string[];
}

const aggregateBySku = (items: QueueItem[]): ToteSkuLine[] => {
    const by = new Map<string, ToteSkuLine>();
    for (const it of items) {
        const cur = by.get(it.sku);
        if (cur) {
            cur.qty += Number(it.qty || 0);
            if (!cur.thumbnailUrl && it.thumbnailUrl) cur.thumbnailUrl = it.thumbnailUrl;
            if (!cur.imageUrl && it.imageUrl) cur.imageUrl = it.imageUrl;
        } else {
            by.set(it.sku, {
                sku: it.sku,
                qty: Number(it.qty || 0),
                title: it.title ?? null,
                thumbnailUrl: it.thumbnailUrl ?? null,
                imageUrl: it.imageUrl ?? null,
                barcodes: it.barcodes ?? [],
            });
        }
    }
    return [...by.values()];
};

// Reconcile one SKU's claim rows to the aggregate remaining quantity
// reported by item-complete. Rows are consumed in queue order so their
// source-location metadata remains intact for every surviving unit.
const setSkuRemaining = (items: QueueItem[], sku: string, remaining: number): QueueItem[] => {
    let unitsToKeep = Math.max(0, remaining);
    return items.flatMap(it => {
        if (it.sku !== sku) return [it];
        const nextQty = Math.min(Math.max(0, Number(it.qty || 0)), unitsToKeep);
        unitsToKeep -= nextQty;
        return nextQty > 0 ? [{ ...it, qty: nextQty }] : [];
    });
};

interface Props {
    module: DropModule;
    titleKey: string;
    putAwayCount: number;
    itemsCount: number;
    onBack: () => void;
    onCounterChange: (n: number) => void;
}

const DropPutAwayView = ({ module, titleKey, putAwayCount, itemsCount, onBack, onCounterChange }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [totes, setTotes] = useState<QueueTote[]>([]);
    // Gates the "queue empty" render until the first /drop/put-away/queue
    // fetch resolves. Without this, toggling from scan → put-away mounts
    // this component with totes=[] and flashes "All caught up" for one
    // frame before the fetch lands, even when the queue has totes.
    const [loaded, setLoaded] = useState(false);
    const [phase, setPhase] = useState<Phase>("awaiting-tote");
    const [activeToteId, setActiveToteId] = useState<string | null>(null);
    // Item-level flow: the tote SKU the picker scanned, awaiting its
    // target location. Null outside awaiting-item-location.
    const [pickedSku, setPickedSku] = useState<ToteSkuLine | null>(null);
    // Wrong-queue prompt: a scanned tote that isn't in this module's list
    // but is awaiting put-away elsewhere (other Drop module, goods-in Put
    // Away) or sits on a picking wave — names its home, offers to go there.
    const [wrongQueue, setWrongQueue] = useState<(WrongQueueHome & { toteId: string }) | null>(null);
    const [pickedBarcode, setPickedBarcode] = useState<string | null>(null);
    const [lastPutAway, setLastPutAway] = useState<LastPutAwayItem | null>(null);
    // Whether item scans are accepted at the scan-location step. Same
    // warehouse setting the main Put Away module gates its item-level
    // flow on. Defaults false until resolved (safer — warehouses with
    // it off can't accidentally trigger the item flow).
    const [allowItemLevel, setAllowItemLevel] = useState(false);
    const [scanValue, setScanValue] = useState("");
    const [scanStatus, setScanStatus] = useState<ScanStatus>("idle");
    const [submitting, setSubmitting] = useState(false);
    // displayName is the target location's human-readable name (shown in
    // the prompt); locationId stays the raw barcode because the
    // rescan-to-confirm path compares scans against it.
    const [confirm, setConfirm] = useState<{ locationId: string; displayName?: string; units: number } | null>(null);
    const confirmingRef = useRef(false);
    // When the confirm prompt opened — scans matching the prompt's location
    // inside CONFIRM_ARM_MS of this are echoes of the scan that opened it
    // (double-fire / held-scan replay), not the operator's confirmation.
    const confirmOpenedAtRef = useRef(0);
    const demo = useDemoVisible();

    const flashTimerRef = useRef<number | null>(null);
    const flash = useCallback((s: Exclude<ScanStatus, "idle">, durationMs: number = 900) => {
        setScanStatus(s);
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = window.setTimeout(() => setScanStatus("idle"), durationMs) as unknown as number;
    }, []);
    // Separate timer for the "tote landed" hold so the active-tote
    // view stays put for the duration of the success tick instead of
    // snapping back to the queue list mid-flash.
    const completionTimerRef = useRef<number | null>(null);
    useEffect(() => () => {
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
        if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/drop/settings?module=${module}`);
                if (!res.ok || !alive) return;
                const d = await res.json();
                if (alive && typeof d.allow_item_level_put_away === "boolean") {
                    setAllowItemLevel(d.allow_item_level_put_away);
                }
            } catch { /* stay at default (off) */ }
        })();
        return () => { alive = false; };
    }, [module]);

    const refresh = useCallback(async () => {
        try {
            const res = await apiFetch(`/drop/put-away/queue?module=${module}`);
            if (!res.ok) return;
            const d = await res.json();
            setTotes(d.totes ?? []);
            onCounterChange(d.count ?? d.totes?.length ?? 0);
        } catch { /* silent */ } finally {
            setLoaded(true);
        }
    }, [module, onCounterChange]);

    useEffect(() => { refresh(); }, [refresh]);

    const activeTote = totes.find(t => t.tote_id === activeToteId) ?? null;
    const activeSkuLines = activeTote ? aggregateBySku(activeTote.items) : [];

    // Shared "tote is done" finale: amber tick + finalised sound, local
    // queue prune while the tick covers the screen, then snap back to
    // the tote list (refresh reconciles with the BE).
    const finishTote = useCallback((toteId: string) => {
        playToteFinalised(); playSuccess(); flash("complete", 750);
        const remainingTotes = totes.filter(qt => qt.tote_id !== toteId);
        setTotes(remainingTotes);
        onCounterChange(remainingTotes.length);
        if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
        completionTimerRef.current = window.setTimeout(() => {
            completionTimerRef.current = null;
            setActiveToteId(null);
            setPickedSku(null);
            setPickedBarcode(null);
            setPhase("awaiting-tote");
            refresh();
            setSubmitting(false);
        }, 750) as unknown as number;
    }, [totes, flash, onCounterChange, refresh]);

    // Map a put-away failure reason to its toast. Shared by the
    // full-tote and item-level completion paths. `unknown_location`
    // softens to "not recognised" when item scans are also valid at
    // that step (the scan could have been a failed item attempt).
    const putAwayErrorToast = useCallback((body: { reason?: string; locationWarehouse?: string }, itemScanWasValid: boolean) => {
        const key =
            body.reason === "unknown_location"            ? (itemScanWasValid ? "scan_not_recognised" : "drop_pa_unknown_location") :
            body.reason === "not_a_low_location"          ? "drop_pa_not_low" :
            body.reason === "target_is_shelf_drop_source" ? "drop_pa_target_is_source" :
            body.reason === "not_a_storage_location"      ? "drop_pa_not_storage" :
            body.reason === "tote_not_in_put_away_queue"  ? "drop_pa_tote_not_in_queue" :
            body.reason === "item_not_in_tote"            ? "drop_pa_item_gone" :
            "drop_pa_failed";
        // wrong_warehouse carries the owning warehouse — needs
        // interpolation, so it can't go through the plain key map.
        const msg = body.reason === "wrong_warehouse"
            ? t("scan_wrong_warehouse", { warehouse: body.locationWarehouse ?? "?" })
            : t(key);
        toast.error(msg, { toastId: `drop-pa-${body.reason}` });
    }, [t]);

    // Returns true when the finale owns the submitting state until its hold
    // finishes; false means the caller can re-enable scanning immediately.
    const completeConfirmedPutAway = useCallback(async (): Promise<boolean> => {
        const pending = confirm;
        if (!pending || !activeToteId || confirmingRef.current) return false;
        confirmingRef.current = true;
        try {
            const res = await apiFetch("/drop/put-away/complete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ module, toteId: activeToteId, locationId: pending.locationId }),
            });
            const body = await res.json();
            if (!body.ok) {
                playError(); flash("error");
                putAwayErrorToast(body, allowItemLevel);
                setConfirm(null);
                return false;
            }
            setConfirm(null);
            finishTote(activeToteId);
            return true;
        } catch {
            setConfirm(null);
            playError(); flash("error");
            toast.error(t("server_error"), { toastId: "drop-pa-net" });
            return false;
        } finally {
            confirmingRef.current = false;
        }
    }, [confirm, activeToteId, module, allowItemLevel, flash, finishTote, putAwayErrorToast, t]);

    const confirmPutAway = useCallback(async () => {
        if (submitting || confirmingRef.current) return;
        setSubmitting(true);
        const holdingForCompletion = await completeConfirmedPutAway();
        if (!holdingForCompletion) setSubmitting(false);
    }, [submitting, completeConfirmedPutAway]);

    const cancelConfirm = useCallback(() => {
        if (submitting || confirmingRef.current) return;
        setConfirm(null);
    }, [submitting]);

    const submitScan = useCallback(async (raw?: string) => {
        const candidate = raw ?? scanValue;
        const barcode = candidate.trim();
        if (!barcode || submitting) return;
        setSubmitting(true);
        // Set in the completion success branches to keep `submitting`
        // (and therefore the disabled scan input) held until the
        // completion timer reverts state. Otherwise the finally would
        // re-enable scans immediately, defeating the visual hold.
        let holdingForCompletion = false;
        try {
            if (confirm) {
                if (barcode === confirm.locationId) {
                    // Swallow the echo of the scan that opened this prompt —
                    // see confirmArming.ts. Silent: no error, prompt stays.
                    if (performance.now() - confirmOpenedAtRef.current < CONFIRM_ARM_MS) return;
                    holdingForCompletion = await completeConfirmedPutAway();
                } else {
                    playError(); flash("error");
                    toast.error(t("put_away_confirm_rescan_mismatch"), {
                        toastId: "drop-pa-confirm-mismatch",
                    });
                }
                return;
            }

            // Rescanning the active tote exits back to the tote list —
            // a local UI close; the tote keeps its stock and its place
            // in the queue. Mirrors the main Put Away page.
            if (activeToteId && barcode === activeToteId) {
                setActiveToteId(null);
                setPickedSku(null);
                setPickedBarcode(null);
                setPhase("awaiting-tote");
                playBoxAdd();
                toast.info(t("put_away_tote_closed"), { toastId: "drop-pa-tote-closed" });
                return;
            }

            if (phase === "awaiting-tote") {
                const found = totes.find(t => t.tote_id === barcode);
                if (!found) {
                    playError(); flash("error");
                    // Not ours — but is it another queue's? One lookup on
                    // the miss path only; the happy path stays local.
                    const home = await lookupWrongQueue(barcode, module);
                    if (home) {
                        setWrongQueue({ toteId: barcode, ...home });
                        return;
                    }
                    toast.error(t("drop_pa_tote_not_in_queue"), { toastId: "drop-pa-no-tote" });
                    return;
                }
                // No green tick — the audio cue plus the instant swap to
                // the active-tote view IS the feedback. The lingering
                // 900ms tick made this purely-local transition feel
                // slower than it is.
                playBoxAdd();
                setActiveToteId(barcode);
                setPhase("awaiting-location");
                return;
            }

            if (phase === "awaiting-location") {
                if (!activeToteId) return;
                // Item-level: the scan matches one of this tote's SKUs
                // (by SKU or any linked barcode) — pick it and ask for
                // its location. Purely local; the BE re-validates at
                // item-complete.
                if (allowItemLevel) {
                    const line = activeSkuLines.find(l =>
                        barcode === l.sku || l.barcodes.includes(barcode)
                    );
                    if (line) {
                        setPickedSku(line);
                        setPickedBarcode(barcode);
                        setPhase("awaiting-item-location");
                        playBoxAdd();
                        return;
                    }
                }
                const res = await apiFetch("/drop/put-away/check-location", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ module, locationId: barcode }),
                });
                const body = await res.json();
                if (!body.ok) {
                    playError(); flash("error");
                    putAwayErrorToast(body, allowItemLevel);
                    return;
                }
                playBoxAdd();
                confirmOpenedAtRef.current = performance.now();
                setConfirm({
                    locationId: barcode,
                    displayName: typeof body.displayName === "string" && body.displayName
                        ? body.displayName
                        : undefined,
                    units: activeSkuLines.reduce((sum, line) => sum + Number(line.qty || 0), 0),
                });
                return;
            }

            if (phase === "awaiting-item-location") {
                if (!activeToteId || !pickedSku) return;
                // Rescanning the picked item (double-fire / habit) is a
                // harmless confirm — accept silently.
                if (barcode === pickedSku.sku || pickedSku.barcodes.includes(barcode)) {
                    playBoxAdd();
                    return;
                }
                // Scanning a DIFFERENT item from the tote retargets the
                // pick — the operator changed their mind about which SKU
                // to place first. Friendlier than failing the scan as an
                // unknown location.
                const otherLine = activeSkuLines.find(l =>
                    barcode === l.sku || l.barcodes.includes(barcode)
                );
                if (otherLine) {
                    setPickedSku(otherLine);
                    setPickedBarcode(barcode);
                    playBoxAdd();
                    return;
                }
                const res = await apiFetch("/drop/put-away/item-complete", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ module, toteId: activeToteId, sku: pickedSku.sku, locationId: barcode }),
                });
                const body = await res.json();
                if (!body.ok) {
                    playError(); flash("error");
                    putAwayErrorToast(body, false);
                    // The item vanished from the tote (another operator
                    // finished it / the queue moved on) — drop back to
                    // the scan-item-or-location step against fresh data.
                    if (body.reason === "item_not_in_tote") {
                        setPickedSku(null);
                        setPickedBarcode(null);
                        setPhase("awaiting-location");
                        refresh();
                    }
                    return;
                }
                setLastPutAway({
                    sku: pickedSku.sku,
                    title: pickedSku.title,
                    barcode: pickedBarcode ?? pickedSku.sku,
                    thumbnailUrl: pickedSku.thumbnailUrl,
                    imageUrl: pickedSku.imageUrl,
                });
                if (body.toteEmpty) {
                    // Last SKU out — same finale as a full-tote release.
                    holdingForCompletion = true;
                    finishTote(activeToteId);
                    return;
                }
                // One unit landed; tote still has stock. Use the BE's
                // remaining count when available, falling back to the
                // local aggregate minus one for compatibility with older
                // backends, then reconcile via refresh().
                playSuccess();
                const skuRemaining = Number.isInteger(body.skuRemaining)
                    ? Math.max(0, body.skuRemaining)
                    : Math.max(0, pickedSku.qty - 1);
                setTotes(prev => prev.map(qt =>
                    qt.tote_id === activeToteId
                        ? { ...qt, items: setSkuRemaining(qt.items, pickedSku.sku, skuRemaining) }
                        : qt
                ));
                setPickedSku(null);
                setPickedBarcode(null);
                setPhase("awaiting-location");
                refresh();
                return;
            }
        } catch {
            playError(); flash("error");
            toast.error(t("server_error"), { toastId: "drop-pa-net" });
        } finally {
            if (!holdingForCompletion) setSubmitting(false);
        }
    }, [scanValue, submitting, confirm, phase, totes, activeToteId, activeSkuLines, allowItemLevel,
        pickedSku, pickedBarcode, module, t, flash, refresh, finishTote, putAwayErrorToast,
        completeConfirmedPutAway]);

    const subPrompt =
        phase === "awaiting-tote"
            ? t("drop_pa_scan_tote")
            : phase === "awaiting-item-location"
                ? t("drop_pa_scan_target_location")
                : allowItemLevel
                    ? t("drop_pa_scan_item_or_location")
                    : t("drop_pa_scan_target_location");
    const commandWords = subPrompt.toUpperCase().split(/[\s-]+/).filter(Boolean);

    return (
        <div className="scan-panel picking-active drop-page">
            {wrongQueue && (
                <WrongQueueModal
                    toteId={wrongQueue.toteId}
                    queue={wrongQueue.queue}
                    info={wrongQueue.info}
                    onGo={(path) => { setWrongQueue(null); navigate(path); }}
                    onClose={() => setWrongQueue(null)}
                />
            )}
            <div className="page-title-bar picking-selector-header">
                <h1 className="page-title">{t(titleKey)}</h1>
                <DropViewToggle
                    view="put-away"
                    putAwayCount={putAwayCount}
                    itemsCount={itemsCount}
                    onChange={(next) => { if (next === "scan") onBack(); }}
                />
                <button
                    className="page-back-btn"
                    onClick={() => navigate("/pick-face")}
                    aria-label="Back to Pick Face"
                >
                    <ArrowLeft size={20} />
                </button>
            </div>

            <div className="scan-panel picking-active" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <ScanFlashOverlay active={scanStatus === "error"} />
                {scanStatus === "complete" ? (
                    /* Post-put-away hold: while the amber tick plays,
                       hide every other element so the picker's focus
                       is entirely on the success cue. The 750 ms hold
                       in finishTote keeps state pinned until the tick
                       finishes; then phase reverts and the regular
                       view returns. */
                    <div className="picking-complete">
                        <div className="drop-complete-stage">
                            <ScanStatusIcons status="complete" />
                        </div>
                    </div>
                ) : !loaded ? (
                    /* Initial fetch hasn't resolved yet. Hold an empty
                       shell so the layout doesn't jump when content
                       arrives, and — crucially — don't flash the
                       "queue empty" message before we know whether
                       there are totes to put away. */
                    <div className="picking-complete" />
                ) : totes.length === 0 ? (
                    /* Empty put-away queue. Fades in (matches the
                       .drop-all-caught-up treatment on the scan view's
                       complete state) so the picker reads it as the
                       resting "all done here" state rather than a
                       loading flash. */
                    <div className="picking-complete">
                        <div className="drop-pa-empty">
                            {t("drop_put_away_queue_empty")}
                        </div>
                    </div>
                ) : (
                    <div className={"drop-pa-scan-view" + (scanStatus === "error" ? " scan-flash-hidden" : "")}>
                        {phase === "awaiting-tote" ? (
                            <>
                                <div className="picking-sub-prompt">{subPrompt}</div>
                                <ul className="drop-pa-tote-list">
                                    {totes.map(tote => {
                                        const itemCount = tote.items.reduce((s, it) => s + Number(it.qty || 0), 0);
                                        return (
                                            <li key={tote.tote_id} className="drop-pa-tote-card">
                                                <PackageOpen size={20} strokeWidth={1.75} className="drop-pa-tote-icon" />
                                                <span className="drop-pa-tote-id">{tote.tote_id}</span>
                                                <span className="drop-pa-tote-count" aria-label={t("drop_pa_tote_item_count", { n: String(itemCount) })}>
                                                    {itemCount}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        ) : (
                            <>
                                <div className="drop-pa-active-tote">
                                    <PackageOpen size={28} strokeWidth={1.75} />
                                    <span>{activeTote?.tote_id ?? ""}</span>
                                </div>
                                <div className="scan-command-wrap">
                                    <div className="scan-command">
                                        {commandWords.map((word, i) => (
                                            <FitWord key={i}>{word}</FitWord>
                                        ))}
                                    </div>
                                    {phase === "awaiting-item-location" && pickedSku && (
                                        <ScannedItemCard
                                            title={pickedSku.title}
                                            barcode={pickedBarcode ?? pickedSku.sku}
                                            thumbnailUrl={pickedSku.thumbnailUrl}
                                            imageUrl={pickedSku.imageUrl}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                        {/* No per-scan tick here: tote/item-scan feedback
                            is the audio cue + the instant view swap;
                            errors render via ScanFlashOverlay; the
                            put-away "complete" tick has its own
                            full-screen branch above. */}
                    </div>
                )}

                <HiddenScanInput
                    value={scanValue}
                    onChange={setScanValue}
                    onSubmit={submitScan}
                    disabled={submitting || totes.length === 0}
                    disableCamera
                    hideToggles
                />

                <LastPutAwayBanner item={lastPutAway} />

                {confirm && (
                    <div className="modal-overlay book-in-modal-overlay" onClick={cancelConfirm}>
                        <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>{t("put_away_confirm_title")}</h2>
                                <button
                                    className="modal-close"
                                    onClick={cancelConfirm}
                                    disabled={submitting}
                                    aria-label={t("cancel")}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="modal-card-body">
                                <div className="book-in-close-card">
                                    <div className="book-in-close-card-icon">
                                        <MapPin size={32} />
                                    </div>
                                    <div className="book-in-close-card-id">{confirm.displayName || confirm.locationId}</div>
                                    <div className="book-in-close-card-divider" />
                                    <div className="book-in-close-card-stats">
                                        <span className="book-in-close-card-count">{confirm.units}</span>
                                        <span className="book-in-close-card-count-label">
                                            {confirm.units === 1 ? t("item_singular") : t("item_plural")}
                                        </span>
                                    </div>
                                </div>
                                <p className="book-in-close-hint">
                                    {t("put_away_confirm_hint", {
                                        location: confirm.displayName || confirm.locationId,
                                        units: confirm.units,
                                    })}
                                </p>
                                <div className="modal-confirm-buttons">
                                    <button
                                        className="btn btn-ghost btn-lg"
                                        onClick={cancelConfirm}
                                        disabled={submitting}
                                    >
                                        {t("cancel")}
                                    </button>
                                    <button
                                        className="btn btn-success btn-lg"
                                        onClick={confirmPutAway}
                                        disabled={submitting}
                                    >
                                        {submitting ? t("submitting") : t("put_away_confirm_button")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Demo panel — only when admin Demo Mode is on. Phase-
                    aware: in awaiting-tote shows each pickable tote ID
                    from the queue; in awaiting-location shows the tote's
                    items (item-level) plus the low pick-face targets and
                    a high (shelf_drop=true) button that should be
                    rejected; in awaiting-item-location just the location
                    targets. */}
                {demo && totes.length > 0 && (
                    <div className="book-in-demo-panel">
                        <span className="book-in-demo-label">Demo</span>

                        {phase === "awaiting-tote" && totes.map(tote => (
                            <button
                                key={tote.tote_id}
                                type="button"
                                className="book-in-demo-btn book-in-demo-btn--tote"
                                onClick={() => submitScan(tote.tote_id)}
                                title={`Tote awaiting put-away (${tote.items.length} SKUs)`}
                            >
                                {tote.tote_id}
                            </button>
                        ))}

                        {phase === "awaiting-location" && (
                            <>
                                {allowItemLevel && activeSkuLines.slice(0, 4).map(line => (
                                    <button
                                        key={line.sku}
                                        type="button"
                                        className="book-in-demo-btn"
                                        onClick={() => submitScan(line.barcodes[0] ?? line.sku)}
                                        title="Pick this item for item-level put-away"
                                    >
                                        {line.sku}
                                    </button>
                                ))}
                                {DEMO_LOW_LOCATIONS.map(loc => (
                                    <button
                                        key={loc}
                                        type="button"
                                        className="book-in-demo-btn"
                                        onClick={() => submitScan(loc)}
                                        title="Valid low pick-face target"
                                    >
                                        {loc}
                                    </button>
                                ))}
                                {DEMO_HIGH_LOCATIONS.map(loc => (
                                    <button
                                        key={loc}
                                        type="button"
                                        className="book-in-demo-btn book-in-demo-btn--bad"
                                        onClick={() => submitScan(loc)}
                                        title="High shelf-drop reserve — should reject (not a low)"
                                    >
                                        {loc}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="book-in-demo-btn book-in-demo-btn--bad"
                                    onClick={() => submitScan("WRONG-LOC")}
                                    title="Unknown location — should reject"
                                >
                                    wrong
                                </button>
                                {activeToteId && (
                                    <button
                                        type="button"
                                        className="book-in-demo-btn book-in-demo-btn--tote"
                                        onClick={() => submitScan(activeToteId)}
                                        title="Rescan the tote to exit back to the list"
                                    >
                                        Close {activeToteId}
                                    </button>
                                )}
                            </>
                        )}

                        {phase === "awaiting-item-location" && (
                            <>
                                {DEMO_LOW_LOCATIONS.map(loc => (
                                    <button
                                        key={loc}
                                        type="button"
                                        className="book-in-demo-btn"
                                        onClick={() => submitScan(loc)}
                                        title="Valid low pick-face target"
                                    >
                                        {loc}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="book-in-demo-btn book-in-demo-btn--bad"
                                    onClick={() => submitScan("WRONG-LOC")}
                                    title="Unknown location — should reject"
                                >
                                    wrong
                                </button>
                                {activeToteId && (
                                    <button
                                        type="button"
                                        className="book-in-demo-btn book-in-demo-btn--tote"
                                        onClick={() => submitScan(activeToteId)}
                                        title="Rescan the tote to exit back to the list"
                                    >
                                        Close {activeToteId}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DropPutAwayView;
